import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, NgZone, ViewEncapsulation, computed, inject, input, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectComponent } from '../../../forms/select/select.component';
import { findScrollContainer, requestScrollPadding, stickyInset, stuckToBottom } from '../content-table/sticky-observer';

export interface PaginationInfo {
  total: number;
  pageSize: number;
  totalPages: number;
  page: number;
}

interface PageSizeOption {
  label: string;
  value: number;
}

/**
 * Table footer: "1–10 of 52" on the left; the page-size select and
 * Previous / page numbers / Next on the right. It keeps one height whether
 * there is one page or many, so filtering never moves what is below it.
 * While the table's end is below the visible area it stays at the bottom of
 * the scroll area (CSS sticky), and settles under the last row once that
 * comes into view.
 */
@Component({
  selector: 'app-content-paginator-cabinet',
  templateUrl: './content-paginator.component.html',
  styleUrl: './content-paginator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  imports: [SelectComponent, FormsModule],
  host: {
    '[class.is-sticky]': 'sticky()',
  },
})
export class ContentPaginatorCabinetComponent implements AfterViewInit {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  /** Keeps the footer at the bottom of the scroll area while the table runs past it. On by default. */
  sticky = input(true);

  /** @deprecated the current page comes from `pagination.page` */
  page = input<number>(1);
  pagination = input.required<PaginationInfo>();
  pageChange = output<number>();
  /** Rows per page; the select is shown only when this is bound. */
  pageSize = model<number | null>(null);
  pageSizeOptions = input<number[]>([10, 25, 50]);
  /** Accessible name of the pagination landmark. */
  ariaLabel = input<string>('Pagination');

  /** The pagination with every field coerced to a number (values can arrive as strings from the URL). */
  readonly info = computed<PaginationInfo>(() => {
    const p = this.pagination();
    return {
      page: Number(p?.page) || 1,
      pageSize: Number(p?.pageSize) || 0,
      total: Number(p?.total) || 0,
      totalPages: Number(p?.totalPages) || 0,
    };
  });

  readonly rangeLabel = computed(() => {
    const { page, pageSize, total } = this.info();
    if (!total) return '0 results';
    const first = (page - 1) * pageSize + 1;
    const last = Math.min(page * pageSize, total);
    return `${first}–${last} of ${total}`;
  });

  readonly multiPage = computed(() => this.info().totalPages > 1);

  readonly sizeOptions = computed<PageSizeOption[]>(() =>
    this.pageSizeOptions().map((value) => ({ label: `${value} per page`, value })),
  );

  readonly pageNumbers = computed<number[]>(() => {
    const { page: currentPage, totalPages } = this.info();
    if (!totalPages) return [];
    const delta = 2;
    let start = Math.max(1, currentPage - delta);
    let end = Math.min(totalPages, currentPage + delta);
    // Show five pages whenever there are that many.
    if (end - start < 4) {
      if (start === 1) {
        end = Math.min(totalPages, start + 4);
      } else if (end === totalPages) {
        start = Math.max(1, end - 4);
      }
    }
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  });

  get pageMin(): number {
    const p = this.info();
    return Math.min(p.page * p.pageSize, p.total);
  }

  onPageChange(newPage: number): void {
    const { totalPages, page } = this.info();
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    this.pageChange.emit(newPage);
  }

  onPageSizeChange(value: unknown): void {
    const size = Number(value);
    if (!size || size === this.pageSize()) return;
    this.pageSize.set(size);
  }

  ngAfterViewInit(): void {
    if (typeof IntersectionObserver === 'undefined') return;
    this.zone.runOutsideAngular(() => this.watchStuck());
  }

  /**
   * Notices when the footer is stuck to the bottom edge (an IntersectionObserver,
   * no scroll events): the stuck look and the scroll padding that keeps a
   * focused row clear of the footer apply only then.
   */
  private watchStuck(): void {
    const host = this.elementRef.nativeElement;
    const scroller = findScrollContainer(host);
    const paddingTarget = scroller ?? host.ownerDocument.documentElement;
    host.style.setProperty('--_stick-bottom', `${stickyInset(scroller, 'bottom')}px`);
    let stuck = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        const next = this.sticky() && !!entry && stuckToBottom(entry);
        if (next === stuck) return;
        stuck = next;
        host.classList.toggle('is-stuck', stuck);
        requestScrollPadding(paddingTarget, 'bottom', this, stuck ? host.getBoundingClientRect().height : null);
      },
      { root: scroller, rootMargin: '0px 0px -1px 0px', threshold: [0, 1] },
    );
    io.observe(host);
    this.destroyRef.onDestroy(() => {
      io.disconnect();
      requestScrollPadding(paddingTarget, 'bottom', this, null);
    });
  }

  /** @deprecated use `pageNumbers` */
  getPageNumbers(): number[] {
    return this.pageNumbers();
  }
}
